import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getAdminClients, listPriorAuths, getExpiringPriorAuths, snoozePriorAuth } from '../utils/api';
import { PageHeader, Panel, StatusPill, Empty, Skeleton, Icon, SortTh, useSort, useToast, fmtDate, fmtNum } from '../components/ui';
import { UNITS_PER_HOUR } from '../utils/claimMath';

const TABS = [['all', 'All'], ['expiring', 'Expiring 30d'], ['expired', 'Expired'], ['pending', 'Pending review']];

// The API sends hoursPerWeek; older records only have unitsPerWeek (15 minute units).
function hoursPerWeekOf(a) {
  if (a.hoursPerWeek !== '' && a.hoursPerWeek != null) return Number(a.hoursPerWeek);
  const n = Number(a.unitsPerWeek);
  return a.unitsPerWeek !== '' && a.unitsPerWeek != null && Number.isFinite(n) ? n / UNITS_PER_HOUR : null;
}
const show = (v, suffix = '') => (v === '' || v == null ? <span className="muted">Not set</span> : `${v}${suffix}`);

function daysUntil(iso) {
  if (!iso) return null;
  return Math.round((new Date(`${iso}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
}

export default function AuthorizationsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'all';
  const q = params.get('q') || '';
  const openId = params.get('auth') || '';
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [showToast, toastNode] = useToast();
  const { sort, onSort, apply } = useSort('endDate', 'asc');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ clients = [] }, exp] = await Promise.all([getAdminClients(), getExpiringPriorAuths().catch(() => ({ authorizations: [] }))]);
        const lists = await Promise.all(clients.map(c => listPriorAuths(c.clientId).then(r => (r.authorizations || []).map(a => ({ ...a, clientId: c.clientId, clientName: c.clientName }))).catch(() => [])));
        const snoozed = Object.fromEntries((exp.authorizations || []).map(a => [a.authId, a]));
        const all = lists.flat().map(a => {
          const d = daysUntil(a.endDate);
          const derived = a.status === 'pending_review' ? 'pending_review' : d != null && d < 0 ? 'expired' : d != null && d <= 30 ? 'expiring' : 'active';
          return { ...a, days: d, derived, alerting: !!snoozed[a.authId] };
        });
        if (!cancelled) setRows(all);
      } catch (e) { if (!cancelled) { setError(e.message || 'Could not load authorizations'); setRows([]); } }
    })();
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => {
    const r = rows || [];
    return { all: r.length, expiring: r.filter(a => a.derived === 'expiring').length, expired: r.filter(a => a.derived === 'expired').length, pending: r.filter(a => a.derived === 'pending_review').length };
  }, [rows]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows || [])
      .filter(a => tab === 'all' || (tab === 'expiring' && a.derived === 'expiring') || (tab === 'expired' && a.derived === 'expired') || (tab === 'pending' && a.derived === 'pending_review'))
      .filter(a => !needle || (a.clientName || '').toLowerCase().includes(needle) || (a.authNumber || '').toLowerCase().includes(needle) || (a.payer || '').toLowerCase().includes(needle));
  }, [rows, tab, q]);
  const sorted = apply(visible, (r, col) => (col === 'endDate' ? r.endDate || null : col === 'units' ? r.unitsAuthorized : r[col]));

  const setParam = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  // The detail drawer is keyed by ?auth=ID so a link can open it directly.
  const selected = useMemo(() => (rows || []).find(a => a.authId === openId) || null, [rows, openId]);
  const openAuth = (a) => setParam('auth', a.authId);
  const closeAuth = () => setParam('auth', '');
  useEffect(() => {
    if (!openId) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setParams(p => { const n = new URLSearchParams(p); n.delete('auth'); return n; }, { replace: true }); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openId, setParams]);
  const onSnooze = async (a, days) => {
    if (!days) return;
    try { await snoozePriorAuth(a.clientId, a.authId, Number(days)); showToast(`Snoozed alerts for ${a.clientName} for ${days} days`); }
    catch (e) { showToast(e.message || 'Snooze failed', 'error'); }
  };

  return (
    <main className="main-content ops-main">
      <div className="ops-page">
        <PageHeader title="Prior Authorizations" subtitle="Every prior authorization across clients, with expiry tracking. Click a row for the full record."
          actions={<Link to="/clients/manage?section=prior-auths" className="ops-btn"><Icon name="upload" /> Upload or review (legacy)</Link>} />
        {error && <div className="ops-error">{error}</div>}
        <Panel flush
          title={<div className="ops-tabs" role="tablist">{TABS.map(([k, l]) => <button key={k} type="button" role="tab" className={tab === k ? 'is-active' : ''} onClick={() => setParam('tab', k === 'all' ? '' : k)}>{l} <span className="count">{counts[k]}</span></button>)}</div>}
          action={<div className="ops-search"><Icon name="search" /><input value={q} onChange={e => setParam('q', e.target.value)} placeholder="Client, auth # or payer" aria-label="Search authorizations" /></div>}>
          {!rows ? <Skeleton rows={6} /> : sorted.length === 0 ? <Empty title="No authorizations">{tab === 'all' ? 'Upload an authorization from a client record.' : 'Nothing in this view.'}</Empty> : (
            <div className="ops-table-wrap">
              <table className="ops-table">
                <thead><tr>
                  <SortTh col="clientName" label="Client" sort={sort} onSort={onSort} />
                  <SortTh col="payer" label="Payer" sort={sort} onSort={onSort} />
                  <th>Auth #</th><th>Service</th>
                  <SortTh col="startDate" label="Start" sort={sort} onSort={onSort} />
                  <SortTh col="endDate" label="End" sort={sort} onSort={onSort} />
                  <SortTh col="units" label="Units" sort={sort} onSort={onSort} className="num" />
                  <th className="num">Units/Week</th><th className="num">Hours/Week</th>
                  <th>Status</th><th></th>
                </tr></thead>
                <tbody>
                  {sorted.map(a => (
                    <tr key={a.authId} className={`is-clickable${selected && selected.authId === a.authId ? ' is-selected' : ''}`} onClick={() => openAuth(a)}>
                      <td><Link to={`/clients/${encodeURIComponent(a.clientId)}/authorizations`} className="ops-cell-main" onClick={e => e.stopPropagation()}>{a.clientName}</Link></td>
                      <td>{a.payer}</td>
                      <td className="mono">{a.authNumber || ''}</td>
                      <td>{a.serviceType || ''}</td>
                      <td>{fmtDate(a.startDate)}</td>
                      <td><div className="ops-cell-main">{fmtDate(a.endDate)}</div>{a.days != null && <div className={`ops-cell-sub${a.days < 0 ? ' neg' : ''}`}>{a.days < 0 ? `${Math.abs(a.days)}d ago` : `in ${a.days}d`}</div>}</td>
                      <td className="num">{a.unitsAuthorized ? fmtNum(a.unitsAuthorized) : ''}{a.unitsPeriod ? <div className="ops-cell-sub">{a.unitsPeriod}</div> : null}</td>
                      <td className="num">{a.unitsPerWeek ? fmtNum(a.unitsPerWeek) : <span className="muted">Not set</span>}</td>
                      <td className="num">{hoursPerWeekOf(a) != null ? fmtNum(hoursPerWeekOf(a), 2) : <span className="muted">Not set</span>}</td>
                      <td><StatusPill status={a.derived} />{a.snoozedUntil && a.snoozedUntil > new Date().toISOString() && <div className="ops-cell-sub">snoozed to {fmtDate(a.snoozedUntil)}</div>}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="ops-actions">
                          <button type="button" className="ops-btn ops-btn--sm" onClick={() => openAuth(a)}>{a.derived === 'pending_review' ? 'Review' : 'Open'}</button>
                          {(a.derived === 'expiring' || a.derived === 'expired') && (
                            <select className="ops-select ops-select--sm" defaultValue="" onChange={e => onSnooze(a, e.target.value)} aria-label="Snooze alerts">
                              <option value="">Snooze</option><option value="3">3 days</option><option value="7">7 days</option><option value="14">14 days</option>
                            </select>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
      {selected && (
        <>
          <div className="ops-drawer__backdrop" onClick={closeAuth} />
          <aside className="ops-drawer ops-drawer--narrow" role="dialog" aria-modal="true" aria-label="Prior authorization">
            <div className="ops-drawer__head">
              <div>
                <div className="ops-label">Prior authorization</div>
                <h2 style={{ margin: '4px 0 2px' }}>{selected.clientName}</h2>
                <div className="ops-cell-sub">{selected.payer}{selected.serviceType ? ` · ${selected.serviceType}` : ''}</div>
              </div>
              <button type="button" className="ops-close" onClick={closeAuth} aria-label="Close"><Icon name="x" /></button>
            </div>
            <div className="ops-drawer__body">
              <dl className="ops-dl">
                <div><dt>Auth #</dt><dd className="mono">{show(selected.authNumber)}</dd></div>
                <div><dt>Status</dt><dd><StatusPill status={selected.derived} />{selected.days != null && <div className={`ops-cell-sub${selected.days < 0 ? ' neg' : ''}`}>{selected.days < 0 ? `Expired ${Math.abs(selected.days)} days ago` : `Expires in ${selected.days} days`}</div>}</dd></div>
                <div><dt>Start</dt><dd>{selected.startDate ? fmtDate(selected.startDate) : show('')}</dd></div>
                <div><dt>End</dt><dd>{selected.endDate ? fmtDate(selected.endDate) : show('')}</dd></div>
                <div><dt>Units</dt><dd>{selected.unitsAuthorized ? `${fmtNum(selected.unitsAuthorized)}${selected.unitsPeriod ? ` ${selected.unitsPeriod}` : ''}` : show('')}</dd></div>
                <div><dt>Units/Week</dt><dd>{selected.unitsPerWeek ? fmtNum(selected.unitsPerWeek) : show('')}</dd></div>
                <div><dt>Hours/Week</dt><dd>{hoursPerWeekOf(selected) != null ? `${fmtNum(hoursPerWeekOf(selected), 2)} hours` : show('')}</dd></div>
                <div><dt>Payer</dt><dd>{show(selected.payer)}</dd></div>
                <div><dt>Service</dt><dd>{show(selected.serviceType)}</dd></div>
                <div><dt>Document</dt><dd>{show(selected.fileName)}</dd></div>
                <div><dt>Uploaded</dt><dd>{selected.uploadedAt ? fmtDate(selected.uploadedAt.slice(0, 10)) : show('')}{selected.uploadedBy ? <div className="ops-cell-sub">by {selected.uploadedBy}</div> : null}</dd></div>
                <div><dt>Alerts</dt><dd>{selected.snoozedUntil && selected.snoozedUntil > new Date().toISOString() ? `Snoozed to ${fmtDate(selected.snoozedUntil.slice(0, 10))}` : selected.alerting ? 'Expiry alert active' : 'None'}</dd></div>
              </dl>
              {selected.derived === 'pending_review' && <div className="ops-note">This authorization was read from the uploaded document and has not been confirmed yet. Review it to correct any field and confirm.</div>}
            </div>
            <div className="ops-drawer__foot">
              <Link to={`/clients/${encodeURIComponent(selected.clientId)}/authorizations`} className="ops-link">Client record</Link>
              <span className="ops-actions">
                <button type="button" className="ops-btn" onClick={closeAuth}>Close</button>
                <Link to={`/clients/manage?open=prior-auth&client=${encodeURIComponent(selected.clientId)}`} className={`ops-btn${selected.derived === 'pending_review' ? ' ops-btn--primary' : ''}`}>{selected.derived === 'pending_review' ? 'Review and confirm' : 'Edit or upload'}</Link>
              </span>
            </div>
          </aside>
        </>
      )}
      {toastNode}
    </main>
  );
}
